import { useEffect, useRef, useState } from "react";
import { translate } from "@kaltura/mediaspace-shared-utils";
import { Button, Modal, ModalContent, ModalTitle } from "@kaltura/ds-react-components";
import RestreamModalForm, { RestreamModalFormHandle } from "./RestreamModalForm";
import RestreamModalDelete from "./RestreamModalDelete";
import { FormData, LiveFeature } from "../RestreamDestinationsTypes";
import { StyledTypography, StyledModalActions } from "./StyledComponents";

export interface RestreamModalProps {
    onAction: (data: any) => void;
    onCancel: () => void;
    action: string;
    processing: boolean;
    restreams: LiveFeature[];
    selectedStream?: LiveFeature;
}

const getTitle = (action: string) => {
    return action === "add"
        ? translate("Add destination")
        : action === "edit"
          ? translate("Edit destination")
          : translate("Delete destination");
};

const getButtonText = (action: string) => {
    return action === "add" || action === "edit" ? translate("Save") : translate("Delete destination");
};

/**
 *  Restream Modal
 */
const RestreamModal = (props: RestreamModalProps) => {
    const { onAction, onCancel, action, processing, restreams, selectedStream } = props;

    const [formData, setFormData] = useState<FormData>({
        systemName: "",
        primaryUrl: "",
        secondaryUrl: "",
        streamKey: "",
    });

    const [isEmptyForm, setIsEmptyForm] = useState<boolean>(true);

    const [submitted, setSubmitted] = useState(false);

    const formRef = useRef<RestreamModalFormHandle>(null);

    const handleOnSubmit = () => {
        if (formRef && formRef.current) {
            formRef.current.submitForm();
            setSubmitted(true);
        }
    };

    useEffect(() => {
        if (selectedStream) {
            setFormData(selectedStream);
        }
    }, [selectedStream]);

    useEffect(() => {
        // formdata is updated on form submit or when selecting a stream for edit
        // the action should run only when submitting
        if (submitted) {
            onAction(formData);
            setSubmitted(false);
        }
    }, [formData]);

    return (
        <Modal open={true} onClose={onCancel} fullWidth={true}>
            <ModalTitle>{getTitle(action)}</ModalTitle>
            <ModalContent>
                {(action === "add" || action === "edit") && (
                    <>
                        <StyledTypography variant={"body2"}>
                            {translate("Fill RTMP endpoint details below.")}
                        </StyledTypography>
                        <RestreamModalForm
                            formRef={formRef}
                            setFormData={setFormData}
                            setIsEmptyForm={setIsEmptyForm}
                            formData={formData}
                            restreams={restreams}
                            selectedStream={selectedStream}
                            action={action}
                        />
                    </>
                )}
                {action === "delete" && <RestreamModalDelete systemName={selectedStream?.systemName} />}
            </ModalContent>
            <StyledModalActions>
                <Button
                    aria-label={translate("Cancel")}
                    onClick={onCancel}
                    key={"cancel"}
                    variant={"borderless"}
                    color={"secondary"}
                >
                    {translate("Cancel")}
                </Button>
                <Button
                    aria-label={action === "delete" ? translate("Delete destination") : translate("Save")}
                    onClick={action === "delete" ? onAction : handleOnSubmit}
                    key={"save"}
                    loading={processing}
                    disabled={processing || (action === "add" && isEmptyForm)}
                >
                    {getButtonText(action)}
                </Button>
            </StyledModalActions>
        </Modal>
    );
};

export default RestreamModal;
